/**
 * CallPage — /call
 *
 * The CallIt experience: live NIFTY market snapshot + community UP/DOWN vote.
 * Two-layer model:
 *   Layer 1  →  Data (Yahoo Finance, free, live)
 *   Layer 2  →  Your call (human opinion, community mood)
 *
 * Nothing here is investment advice. All community sentiment is opinion only.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, ArrowUp, ArrowDown,
  RefreshCw, ExternalLink, Share2, Check
} from 'lucide-react';
import { useIndices } from '../hooks/useIndices';
import { fetchHistorical, type OHLCCandle, type IndexQuote } from '@/services/marketDataApi';

import SeoMeta from './SeoMeta';
// ─── Helpers ─────────────────────────────────────────────────────────────────

function getISTDate(): string {
  const now = new Date();
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().split('T')[0];
}

function isMarketOpen(): boolean {
  const now = new Date();
  const day = now.getUTCDay();
  if (day === 0 || day === 6) return false;
  const utcMins = now.getUTCHours() * 60 + now.getUTCMinutes();
  return utcMins >= 225 && utcMins < 600;
}

function formatPrice(n: number): string {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtChange(n: number): string {
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;
}

function buildSparkPoints(candles: OHLCCandle[], w: number, h: number): string {
  if (candles.length < 2) return '';
  const prices = candles.map(c => c.close);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  return prices
    .map((p, i) => `${(i / (prices.length - 1)) * w},${h - ((p - min) / range) * (h - 4) - 2}`)
    .join(' ');
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const IndexTile: React.FC<{ idx: IndexQuote }> = ({ idx }) => {
  const up = idx.changePct >= 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1">
      <p className="text-[11px] text-slate-500 tracking-wide uppercase font-medium">{idx.name}</p>
      <p className="text-base font-bold font-mono text-slate-900 leading-none">
        {formatPrice(idx.price)}
      </p>
      <p className={`text-xs font-medium ${up ? 'text-green-600' : 'text-red-600'}`}>
        {up ? '+' : ''}{idx.changePct.toFixed(2)}%
      </p>
    </div>
  );
};

function vixLabel(vix: number): { label: string; color: string; bg: string } {
  if (vix < 12) return { label: 'Very calm', color: 'text-green-600', bg: 'bg-green-50 border-green-200' };
  if (vix < 16) return { label: 'Calm', color: 'text-green-600', bg: 'bg-green-50 border-green-200' };
  if (vix < 20) return { label: 'Cautious', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' };
  if (vix < 25) return { label: 'Nervous', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' };
  return { label: 'Fear', color: 'text-red-600', bg: 'bg-red-50 border-red-200' };
}

// ─── Main Component ───────────────────────────────────────────────────────────

type VoteDirection = 'up' | 'down';
interface VoteCounts { up: number; down: number; date: string }

const VOTE_KEY = () => `callit_vote_${getISTDate()}`;

const CallPage: React.FC = () => {
  const { indices, loading: indicesLoading, lastUpdated, refresh } = useIndices();

  const nifty    = indices.find(i => i.symbol === '^NSEI');
  const bankNifty = indices.find(i => i.symbol === '^NSEBANK');
  const sensex   = indices.find(i => i.symbol === '^BSESN');
  const vix      = indices.find(i => i.symbol === '^INDIAVIX');
  const niftyIT  = indices.find(i => i.symbol === '^CNXIT');
  const niftyAuto = indices.find(i => i.symbol === '^CNXAUTO');

  const [candles,   setCandles]   = useState<OHLCCandle[]>([]);
  const [votes,     setVotes]     = useState<VoteCounts | null>(null);
  const [userVote,  setUserVote]  = useState<VoteDirection | null>(null);
  const [voteLoading, setVoteLoading] = useState(false);
  const [shared,    setShared]    = useState(false);
  const marketOpen = isMarketOpen();

  useEffect(() => {
    fetchHistorical('^NSEI', '1d', '5m').then(setCandles).catch(() => {});

    fetch('/.netlify/functions/vote')
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setVotes(d))
      .catch(() => {});

    const stored = localStorage.getItem(VOTE_KEY());
    if (stored === 'up' || stored === 'down') setUserVote(stored);
  }, []);

  const handleVote = useCallback(async (dir: VoteDirection) => {
    if (userVote || voteLoading) return;
    setVoteLoading(true);
    setUserVote(dir);
    localStorage.setItem(VOTE_KEY(), dir);
    try {
      const res = await fetch('/.netlify/functions/vote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction: dir }),
      });
      if (res.ok) setVotes(await res.json());
    } catch { /* keep the previous counts on network error */ }
    setVoteLoading(false);
  }, [userVote, voteLoading]);

  const handleShare = () => {
    const text = userVote
      ? `I'm calling NIFTY ${userVote.toUpperCase()} today! What's your call? call.indianstockmarketgpt.com`
      : `Make your market call at call.indianstockmarketgpt.com`;
    if (navigator.share) {
      navigator.share({ text, url: 'https://call.indianstockmarketgpt.com' }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => { setShared(true); setTimeout(() => setShared(false), 2000); });
    }
  };

  const sparkPoints = useMemo(() => buildSparkPoints(candles, 300, 48), [candles]);
  const isUp = (nifty?.changePct ?? 0) >= 0;

  const rangePercent = useMemo(() => {
    if (!nifty || nifty.high <= nifty.low) return 50;
    return Math.round(((nifty.price - nifty.low) / (nifty.high - nifty.low)) * 100);
  }, [nifty]);

  const total    = (votes?.up ?? 0) + (votes?.down ?? 0);
  const upPct    = total > 0 ? Math.round(((votes!.up ?? 0) / total) * 100) : null;
  const downPct  = upPct !== null ? 100 - upPct : null;

  const secondaryIndices = [bankNifty, sensex, niftyIT, niftyAuto].filter(Boolean) as IndexQuote[];
  const vixInfo = vix ? vixLabel(vix.price) : null;
  const lastUpdatedStr = lastUpdated
    ? `${Math.round((Date.now() - lastUpdated.getTime()) / 1000)}s ago`
    : '';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      <SeoMeta title="Your Call: Nifty Community Mood | IndianStockMarketGPT" description="Share your view on how Nifty 50 closes today and see the community mood. Community opinion only — not a market signal or investment advice." url="https://indianstockmarketgpt.com/call" />

      {/* ── Minimal header ──────────────────────────────────────────────── */}
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-navy-50 border border-navy-100 flex items-center justify-center">
              <TrendingUp className="w-3.5 h-3.5 text-navy-500" />
            </div>
            <span className="font-semibold text-sm text-slate-900 tracking-wide">
              Call<span className="text-navy-500">It</span>
            </span>
            <span className="hidden sm:inline text-slate-400 text-xs">by IndianStockMarketGPT</span>
          </div>
          <Link
            to="/"
            className="text-xs text-slate-500 hover:text-navy-500 transition-colors flex items-center gap-1"
          >
            AI Research <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </header>

      <main className="flex-1 pt-14">
        <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">

          {/* ── Live NIFTY hero ─────────────────────────────────────────── */}
          <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className={`h-1 w-full ${isUp ? 'bg-green-500' : 'bg-red-500'}`} />

            <div className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-slate-500 tracking-widest uppercase font-medium">NIFTY 50</p>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      marketOpen
                        ? 'bg-green-50 text-green-600 border border-green-200'
                        : 'bg-slate-100 text-slate-500 border border-slate-200'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${marketOpen ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`} />
                      {marketOpen ? 'Live' : 'Closed'}
                    </span>
                  </div>

                  {indicesLoading && !nifty ? (
                    <div className="h-10 w-40 bg-slate-100 rounded-lg animate-pulse" />
                  ) : (
                    <p className={`text-4xl sm:text-5xl font-bold font-mono tracking-tight ${isUp ? 'text-green-600' : 'text-red-600'}`}>
                      {nifty ? formatPrice(nifty.price) : '—'}
                    </p>
                  )}

                  {nifty && (
                    <div className="flex items-center gap-2 mt-1.5">
                      {isUp ? <ArrowUp className="w-4 h-4 text-green-600" /> : <ArrowDown className="w-4 h-4 text-red-600" />}
                      <span className={`text-sm font-medium ${isUp ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtChange(nifty.change)} ({fmtChange(nifty.changePct)}%)
                      </span>
                      <span className="text-xs text-slate-400">vs yesterday</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={refresh}
                  className="text-slate-400 hover:text-navy-500 transition-colors p-1.5 rounded-lg hover:bg-slate-100"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {/* Sparkline */}
              {sparkPoints && (
                <div className="mb-4">
                  <svg viewBox="0 0 300 48" preserveAspectRatio="none" className="w-full h-12">
                    <polyline
                      points={sparkPoints}
                      fill="none"
                      stroke={isUp ? '#16a34a' : '#dc2626'}
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}

              {/* Intraday range bar */}
              {nifty && (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-[11px] text-slate-500">
                    <span>Low {formatPrice(nifty.low)}</span>
                    <span className="text-slate-600">Today's range</span>
                    <span>High {formatPrice(nifty.high)}</span>
                  </div>
                  <div className="relative h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 rounded-full ${isUp ? 'bg-green-200' : 'bg-red-200'}`}
                      style={{ width: `${rangePercent}%` }}
                    />
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow ${isUp ? 'bg-green-500' : 'bg-red-500'}`}
                      style={{ left: `calc(${rangePercent}% - 6px)` }}
                    />
                  </div>
                </div>
              )}

              {lastUpdatedStr && (
                <p className="text-[10px] text-slate-400 mt-3">Updated {lastUpdatedStr} · 15-min delayed · Yahoo Finance</p>
              )}
            </div>
          </section>

          {/* ── The Call ────────────────────────────────────────────────── */}
          <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="p-5">
              <p className="text-xs text-navy-500 tracking-widest uppercase font-semibold mb-1">Make Your Call</p>
              <h1 className="text-xl sm:text-2xl font-bold font-serif text-slate-900 mb-1 leading-snug">
                Will NIFTY 50 close <span className="text-navy-500">UP or DOWN</span> today?
              </h1>
              <p className="text-xs text-slate-400 mb-5">
                Community mood · not a market signal · resets at 3:30 PM IST
              </p>

              {!userVote ? (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleVote('up')}
                    disabled={voteLoading}
                    className="group relative flex flex-col items-center gap-2 py-6 px-4 rounded-xl border-2 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-400 transition-all active:scale-[0.98] disabled:opacity-50"
                  >
                    <div className="w-12 h-12 rounded-full bg-green-100 border border-green-200 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                      <ArrowUp className="w-6 h-6 text-green-600" />
                    </div>
                    <span className="text-base font-bold text-green-600">UP ↑</span>
                    <span className="text-[11px] text-slate-500">Closes higher</span>
                  </button>

                  <button
                    onClick={() => handleVote('down')}
                    disabled={voteLoading}
                    className="group relative flex flex-col items-center gap-2 py-6 px-4 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-400 transition-all active:scale-[0.98] disabled:opacity-50"
                  >
                    <div className="w-12 h-12 rounded-full bg-red-100 border border-red-200 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                      <ArrowDown className="w-6 h-6 text-red-600" />
                    </div>
                    <span className="text-base font-bold text-red-600">DOWN ↓</span>
                    <span className="text-[11px] text-slate-500">Closes lower</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className={`flex items-center gap-2.5 p-3 rounded-xl border ${
                    userVote === 'up'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      userVote === 'up' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {userVote === 'up'
                        ? <ArrowUp className="w-4 h-4 text-green-600" />
                        : <ArrowDown className="w-4 h-4 text-red-600" />
                      }
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${userVote === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        You called it {userVote.toUpperCase()} ↑
                      </p>
                      <p className="text-[11px] text-slate-400">Come back at 3:30 PM IST to see the result</p>
                    </div>
                  </div>

                  {upPct !== null && total > 0 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-[11px] text-slate-600 font-medium">
                        <span className="text-green-600">↑ UP {upPct}%</span>
                        <span className="text-slate-400">{total.toLocaleString()} calls today</span>
                        <span className="text-red-600">{downPct}% DOWN ↓</span>
                      </div>
                      <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
                        <div
                          className="bg-green-400 rounded-l-full transition-all duration-700"
                          style={{ width: `${upPct}%` }}
                        />
                        <div
                          className="bg-red-400 rounded-r-full transition-all duration-700"
                          style={{ width: `${downPct}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 text-center">
                        Community mood — not a market signal. Not investment advice.
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleShare}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-slate-200 text-slate-500 hover:text-navy-500 hover:border-navy-300 text-sm font-medium transition-all"
                  >
                    {shared ? <Check className="w-4 h-4 text-green-500" /> : <Share2 className="w-4 h-4" />}
                    {shared ? 'Copied!' : 'Share your call'}
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* ── VIX card ─────────────────────────────────────────────────── */}
          {vix && vixInfo && (
            <section className={`border rounded-xl p-4 flex items-center gap-4 ${vixInfo.bg}`}>
              <div>
                <p className="text-[11px] text-slate-500 uppercase tracking-wide mb-0.5">India VIX · Fear Index</p>
                <p className={`text-2xl font-bold font-mono ${vixInfo.color}`}>{vix.price.toFixed(2)}</p>
              </div>
              <div className="flex-1 border-l border-slate-200 pl-4">
                <p className={`text-sm font-semibold ${vixInfo.color}`}>Market is {vixInfo.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  VIX below 15 = low fear. Above 20 = elevated uncertainty.
                </p>
              </div>
            </section>
          )}

          {/* ── Secondary indices grid ────────────────────────────────────── */}
          {secondaryIndices.length > 0 && (
            <section>
              <p className="text-[11px] text-slate-500 uppercase tracking-widest font-medium mb-3">Live Indices</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {secondaryIndices.map(idx => (
                  <IndexTile key={idx.symbol} idx={idx} />
                ))}
              </div>
            </section>
          )}

          {/* ── Two-layer explainer ──────────────────────────────────────── */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <p className="text-xs text-navy-500 tracking-widest uppercase font-semibold mb-3">How it works</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-slate-100 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">Layer 1</span>
                </div>
                <p className="text-sm font-semibold text-slate-800">AI investigates</p>
                <p className="text-xs text-slate-500 leading-relaxed">Live data, historical context, indicators — no opinion, no advice.</p>
                <Link to="/ask" className="text-xs text-navy-500 hover:text-navy-700 inline-flex items-center gap-1 mt-1">
                  Try the research tool <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <div className="space-y-1 border-l border-slate-100 pl-4">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-navy-50 border border-navy-100 text-navy-600 px-2 py-0.5 rounded-full font-medium">Layer 2</span>
                </div>
                <p className="text-sm font-semibold text-slate-800">You call it</p>
                <p className="text-xs text-slate-500 leading-relaxed">Your opinion. Your call. Community mood, not a signal.</p>
              </div>
            </div>
          </section>

          {/* ── Footer disclaimer ────────────────────────────────────────── */}
          <footer className="text-center pb-4 space-y-2">
            <p className="text-[11px] text-slate-400 leading-relaxed max-w-sm mx-auto">
              For educational purposes only. Not investment advice. Not SEBI registered.
              Community opinion only. Past votes do not predict future market direction.
            </p>
            <Link to="/" className="text-[11px] text-slate-400 hover:text-navy-500 transition-colors">
              ← IndianStockMarketGPT
            </Link>
          </footer>

        </div>
      </main>
    </div>
  );
};

export default CallPage;
